import React from "react";
import Close from "@mui/icons-material/Close";
import CustomTooltip from "../../../../code-connect/component/CustomTooltip";
import {
  PriceBlockIcon,
  PriceCommentIcon,
  PriceDollarIcon,
} from "../../../../icons";
import {
  FavoritePriceShowAs,
  IFavoriteLocationExtension,
  ModeValueRow,
  MonthColumn,
  MonthHalf,
} from "../../../../types/IFavoriteOrigins";
import { formatRequestedAt } from "../FavoriteOriginHelper";
import { formatPriceByShowAs } from "../priceFormatting";
import { getPriceInputState } from "../FavoriteOriginAddOriginUtils";

type ColumnWithSpan = MonthColumn & { colSpan?: number };

export interface PriceCellProps {
  origin: IFavoriteLocationExtension;
  modeRow: ModeValueRow;
  column: ColumnWithSpan;
  rowColumns: ColumnWithSpan[];
  monthModeRows: ModeValueRow[];
  isRowEditing: boolean;
  isEditingPrices: boolean;
  activeCellKey: string | null;
  recentlySavedKeys: Set<string>;
  dragHoverPriceKey: string | null;
  draftPrices: Record<string, string>;
  draftPriceShowAs: Record<string, FavoritePriceShowAs>;
  isNewlyAddedOrigin?: boolean;
  isPriceDragCopying: boolean;
  priceInputRefs: React.MutableRefObject<
    Record<string, HTMLInputElement | null>
  >;
  getPriceInputKey: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half?: MonthHalf,
  ) => string;
  getPriceInputValue: (
    origin: IFavoriteLocationExtension,
    modeRow: ModeValueRow,
    month: string,
    half?: MonthHalf,
  ) => string;
  getPriceShowAsValue: (
    origin: IFavoriteLocationExtension,
    modeRow: ModeValueRow,
    month: string,
    half?: MonthHalf,
  ) => FavoritePriceShowAs | null;
  getDisplayPriceValue: (
    origin: IFavoriteLocationExtension,
    modeRow: ModeValueRow,
    month: string,
    half?: MonthHalf,
  ) => number | null;
  handlePriceCellClick: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half?: MonthHalf,
  ) => void;
  handlePriceInputChange: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half: MonthHalf,
    value: string,
  ) => void;
  handlePriceInputKeyDown: (
    event: React.KeyboardEvent<HTMLInputElement>,
    origin: IFavoriteLocationExtension,
    modeKey: string,
    column: ColumnWithSpan,
    rowColumns: ColumnWithSpan[],
    columnModeRows: ModeValueRow[],
  ) => void;
  handlePriceInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  handlePriceShowAsChange: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half: MonthHalf,
    showAs: FavoritePriceShowAs,
  ) => void;
  handlePriceShowAsChangeAndSave: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half: MonthHalf,
    showAs: "DISCUSS" | "NO_OFFER",
  ) => void;
  handleToggleSplitMonth: (month: string) => void;
  handleRevertEditingPrices: () => void;
  applyDraggedPriceToInput: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half: MonthHalf,
    getPriceInputKey: PriceCellProps["getPriceInputKey"],
  ) => void;
  startPriceDragCopy: (
    event: React.PointerEvent<HTMLButtonElement>,
    origin: IFavoriteLocationExtension,
    modeRow: ModeValueRow,
    month: string,
    half: MonthHalf,
    getPriceInputKey: PriceCellProps["getPriceInputKey"],
    getPriceShowAsValue: PriceCellProps["getPriceShowAsValue"],
    getPriceInputValue: PriceCellProps["getPriceInputValue"],
  ) => void;
}

const PriceCell: React.FC<PriceCellProps> = ({
  origin,
  modeRow,
  column,
  rowColumns,
  monthModeRows,
  isRowEditing,
  isEditingPrices,
  activeCellKey,
  recentlySavedKeys,
  dragHoverPriceKey,
  draftPrices,
  draftPriceShowAs,
  isNewlyAddedOrigin = false,
  isPriceDragCopying,
  priceInputRefs,
  getPriceInputKey,
  getPriceInputValue,
  getPriceShowAsValue,
  getDisplayPriceValue,
  handlePriceCellClick,
  handlePriceInputChange,
  handlePriceInputKeyDown,
  handlePriceInputBlur,
  handlePriceShowAsChange,
  handlePriceShowAsChangeAndSave,
  handleToggleSplitMonth,
  handleRevertEditingPrices,
  applyDraggedPriceToInput,
  startPriceDragCopy,
}) => {
  if (!isRowEditing) {
    const cellKey = getPriceInputKey(
      origin,
      modeRow.key,
      column.month,
      column.half,
    );
    const isSavedHighlight = recentlySavedKeys.has(cellKey);
    const displayShowAs = getPriceShowAsValue(
      origin,
      modeRow,
      column.month,
      column.half,
    );
    const displayValue = getDisplayPriceValue(
      origin,
      modeRow,
      column.month,
      column.half,
    );
    const isDiscuss = displayShowAs === "DISCUSS";
    const isNoOffer = displayShowAs === "NO_OFFER";

    return (
      <CustomTooltip
        variant="dark"
        placement="bottom-start"
        arrow
        title={
          modeRow.requestedBy
            ? `Last edited by ${modeRow.requestedBy}${
                modeRow.requestedAt
                  ? ` · ${formatRequestedAt(modeRow.requestedAt)}`
                  : ""
              }`
            : ""
        }
      >
        <div
          className={`dom-value-line dom-value-line-clickable ${
            displayValue === null && !isDiscuss && !isNoOffer ? "muted" : ""
          }${isDiscuss ? " dom-value-line-discuss" : ""}${
            isNoOffer ? " dom-value-line-no-offer" : ""
          }${isSavedHighlight ? " dom-value-line-saved" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() =>
            handlePriceCellClick(origin, modeRow.key, column.month, column.half)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handlePriceCellClick(
                origin,
                modeRow.key,
                column.month,
                column.half,
              );
            }
          }}
        >
          {formatPriceByShowAs(displayValue, displayShowAs)}
        </div>
      </CustomTooltip>
    );
  }

  // No stored selection means the price is used, matching the persisted default for new origins.
  const currentShowAs =
    getPriceShowAsValue(origin, modeRow, column.month, column.half) ?? "PRICE";
  const allowDiscuss = !origin.competitor;
  const showPostedSplitButton =
    !origin.competitor &&
    modeRow.key === "posted" &&
    (column.half === "full" || (column.isSplit && column.half === "h1"));
  const splitButtonIsActive = column.isSplit && column.half === "h1";
  const isDollarSelected = currentShowAs === "PRICE";
  const isCommentSelected = currentShowAs === "DISCUSS";
  const isBlockSelected = currentShowAs === "NO_OFFER";

  const { priceKey, inputStateClass, isReadOnly } = getPriceInputState({
    origin,
    modeRow,
    month: column.month,
    half: column.half,
    dragHoverPriceKey,
    draftPrices,
    draftPriceShowAs,
    currentShowAs,
    isNewlyAddedOrigin,
    getPriceInputKey,
  });
  const priceInputRefMap = priceInputRefs.current;

  const handleDragOverCell = () => {
    if (!isPriceDragCopying) return;
    applyDraggedPriceToInput(
      origin,
      modeRow.key,
      column.month,
      column.half,
      getPriceInputKey,
    );
  };

  return (
    <div
      className="dom-value-line dom-value-line-editable"
      onPointerEnter={handleDragOverCell}
      onMouseEnter={handleDragOverCell}
      onMouseMove={handleDragOverCell}
      onPointerOver={handleDragOverCell}
    >
      <div
        className={`dom-price-edit-stack${
          !origin.competitor &&
          modeRow.key === "posted" &&
          (column.half === "full" || (column.isSplit && column.half === "h1"))
            ? " dom-price-edit-stack--with-split"
            : ""
        }`}
      >
        {showPostedSplitButton && (
          <CustomTooltip
            title={splitButtonIsActive ? "Unsplit Price" : "Split Price"}
            variant="dark"
            placement="top"
            arrow
            enterDelay={400}
            disableInteractive
            PopperProps={{
              className: "dom-price-drag-copy-tooltip-popper",
              modifiers: [{ name: "darkArrowAlign", enabled: false }],
            }}
          >
            <span className="dom-action-tooltip-trigger">
              <button
                id={`split-button-${column.month}`}
                type="button"
                className={`dom-posted-split-fab${
                  splitButtonIsActive ? " dom-posted-split-fab--active" : ""
                }`}
                aria-label="Split posted price"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleToggleSplitMonth(column.month)}
              >
                <svg
                  className="dom-posted-split-icon"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M4.19375 2.68125L5.625 1.25H1.875V5L3.30625 3.56875L6.875 7.1375V13.7563H8.125V7.1375C8.125 6.80625 7.99375 6.4875 7.75625 6.25625L4.1875 2.6875L4.19375 2.68125ZM10.8063 2.68125L8.30625 5.18125L9.19375 6.06875L11.6937 3.56875L13.125 5V1.28125L9.375 1.25L10.8063 2.68125Z"
                    fill="#F1F5F9"
                  />
                </svg>
              </button>
            </span>
          </CustomTooltip>
        )}
        <div className="dom-price-input-wrap">
          <input
            id={`origin-manager-price-input-${priceKey}`}
            type="text"
            inputMode="decimal"
            className={`dom-price-input${inputStateClass}`}
            placeholder="Price"
            ref={(node) => {
              priceInputRefMap[priceKey] = node;
            }}
            value={getPriceInputValue(
              origin,
              modeRow,
              column.month,
              column.half,
            )}
            onFocus={(event) => {
              if (currentShowAs === "PRICE" && event.currentTarget.value) {
                event.currentTarget.select();
              }
              handlePriceCellClick(
                origin,
                modeRow.key,
                column.month,
                column.half,
              );
            }}
            onChange={(event) => {
              handlePriceInputChange(
                origin,
                modeRow.key,
                column.month,
                column.half,
                event.target.value,
              );
            }}
            onKeyDown={(event) =>
              handlePriceInputKeyDown(
                event,
                origin,
                modeRow.key,
                column,
                rowColumns,
                monthModeRows,
              )
            }
            onBlur={handlePriceInputBlur}
            onPointerEnter={handleDragOverCell}
            onMouseEnter={handleDragOverCell}
            onMouseMove={handleDragOverCell}
            onPointerOver={handleDragOverCell}
            readOnly={isReadOnly}
          />
          <CustomTooltip
            title="Drag to fill"
            variant="dark"
            placement="top"
            arrow
            enterDelay={400}
            disableInteractive
            PopperProps={{
              className: "dom-price-drag-copy-tooltip-popper",
              modifiers: [{ name: "darkArrowAlign", enabled: false }],
            }}
          >
            <span className="dom-action-tooltip-trigger dom-price-drag-copy-tooltip-trigger">
              <button
                type="button"
                className="dom-price-drag-copy-handle"
                aria-label="Drag to copy value"
                onPointerDown={(event) =>
                  startPriceDragCopy(
                    event,
                    origin,
                    modeRow,
                    column.month,
                    column.half,
                    getPriceInputKey,
                    getPriceShowAsValue,
                    getPriceInputValue,
                  )
                }
              />
            </span>
          </CustomTooltip>
          {isEditingPrices && activeCellKey === priceKey && (
            <CustomTooltip
              title="Cancel edit"
              variant="dark"
              placement="bottom-start"
              arrow
            >
              <span className="dom-action-tooltip-trigger">
                <button
                  type="button"
                  className="dom-price-cancel-edit-btn"
                  aria-label="Cancel edit"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleRevertEditingPrices}
                >
                  <Close fontSize="inherit" />
                </button>
              </span>
            </CustomTooltip>
          )}
        </div>
        <div className="dom-price-meta-row">
          <CustomTooltip
            title="Set to Price"
            variant="dark"
            placement="bottom-start"
            arrow
          >
            <span className="dom-action-tooltip-trigger">
              <button
                id={`use-price-btn-${origin.id}-${modeRow.key}-${column.month}-${column.half}`}
                type="button"
                tabIndex={-1}
                className={`dom-price-meta-icon-btn ${
                  isDollarSelected ? "dom-price-meta-money" : ""
                }`}
                aria-label="Use price"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  handlePriceShowAsChange(
                    origin,
                    modeRow.key,
                    column.month,
                    column.half,
                    "PRICE",
                  )
                }
              >
                <PriceDollarIcon
                  color={isDollarSelected ? "#2E7D32" : "#90A1B9"}
                />
              </button>
            </span>
          </CustomTooltip>
          {allowDiscuss && (
            <CustomTooltip
              title="Set to Discuss"
              variant="dark"
              placement="bottom-start"
              arrow
            >
              <span className="dom-action-tooltip-trigger">
                <button
                  type="button"
                  tabIndex={-1}
                  className={`dom-price-meta-icon-btn ${
                    isCommentSelected ? "dom-price-meta-comment" : ""
                  }`}
                  aria-label="Set to Discuss"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() =>
                    handlePriceShowAsChangeAndSave(
                      origin,
                      modeRow.key,
                      column.month,
                      column.half,
                      "DISCUSS",
                    )
                  }
                >
                  <PriceCommentIcon
                    color={isCommentSelected ? "#155DFC" : "#90A1B9"}
                  />
                </button>
              </span>
            </CustomTooltip>
          )}
          <CustomTooltip
            title="Set to No Offer"
            variant="dark"
            placement="bottom-start"
            arrow
          >
            <span className="dom-action-tooltip-trigger">
              <button
                type="button"
                tabIndex={-1}
                className={`dom-price-meta-icon-btn ${
                  isBlockSelected ? "dom-price-meta-block" : ""
                }`}
                aria-label="Set to No Offer"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  handlePriceShowAsChangeAndSave(
                    origin,
                    modeRow.key,
                    column.month,
                    column.half,
                    "NO_OFFER",
                  )
                }
              >
                <PriceBlockIcon
                  color={isBlockSelected ? "#C62828" : "#90A1B9"}
                />
              </button>
            </span>
          </CustomTooltip>
        </div>
      </div>
    </div>
  );
};

export default PriceCell;
